import React, { useEffect, Suspense, lazy } from "react";
import ReactGA from "react-ga4";
import * as Sentry from "@sentry/react";
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import Layout from "./components/layout/Layout";
import LandingPage from "./components/landing/LandingPage";
import ScrollToTop from "./components/layout/ScrollToTop";
import { supportMailto } from './config/contact';
import WebPurchaseRoute from './components/upgrade/WebPurchaseRoute';
import { AuthProvider } from './hooks/useAuth';
import { isEmbeddedOdysseyApp, optionalTelemetryAllowed } from './services/appEnvironment';

// Code-split the heavy routes — keeps initial bundle small.
const ChatPage = lazy(() => import('./components/conversation/ChatPage'));
const UpgradePage = lazy(() => import('./components/upgrade/UpgradePage'));
const ThankYouPage = lazy(() => import('./components/upgrade/ThankYouPage'));
const Login = lazy(() => import('./components/auth/Login'));
const AboutPage = lazy(() => import('./components/about/AboutPage'));
const LegalPage = lazy(() => import('./components/legal/LegalPage'));
const MyTripsPage = lazy(() => import('./components/trips/MyTripsPage'));
const AccountPage = lazy(() => import('./components/account/AccountPage'));
const TripIdeasPage = lazy(() => import('./components/seo/TripIdeasPage'));
const TripGuidePage = lazy(() => import('./components/seo/TripGuidePage'));
const NotFoundPage = lazy(() => import('./components/legal/NotFoundPage'));

const RouteFallback = () => (
  <div
    role="status"
    aria-live="polite"
    style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '60vh',
      color: 'var(--text-muted)',
      fontWeight: 600,
      letterSpacing: '0.04em',
    }}
  >
    <span className="route-fallback-spinner" aria-hidden />
    <span>Loading…</span>
  </div>
);

const ErrorFallback = () => (
  <div className="card-os" style={{ maxWidth: 640, margin: '6rem auto', textAlign: 'center' }}>
    <h2 style={{ marginTop: 0 }}>Something went wrong.</h2>
    <p style={{ color: 'var(--text-secondary)' }}>
      Please reload this page. If it keeps happening,{' '}
      {isEmbeddedOdysseyApp()
        ? 'open Profile and choose Email support.'
        : <>email <a href={supportMailto()}>support</a>.</>}
    </p>
    <button
      type="button"
      className="btn-os btn-os--primary"
      onClick={() => window.location.reload()}
    >
      Reload page
    </button>
  </div>
);

function App() {
  useEffect(() => {
    if (!optionalTelemetryAllowed()) return;
    const gaMeasurementId = process.env.REACT_APP_GA_MEASUREMENT_ID;
    if (gaMeasurementId) {
      ReactGA.initialize(gaMeasurementId);
    } else if (process.env.NODE_ENV !== 'production') {
      console.warn("Google Analytics Measurement ID not found. Analytics disabled.");
    }
  }, []);

  return (
    <Sentry.ErrorBoundary fallback={<ErrorFallback />}>
      <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        {/* Keep browser history stable when the account privacy boundary
            remounts private screens after sign-out or an account switch. */}
        <AuthProvider>
          <ScrollToTop />
          <Layout>
            <Suspense fallback={<RouteFallback />}>
              <Routes>
                <Route path="/" element={<LandingPage />} />
                <Route path="/login" element={<Login />} />
                <Route path="/chat" element={<ChatPage />} />
                <Route path="/trips" element={<MyTripsPage />} />
                <Route path="/account" element={<AccountPage />} />
                <Route path="/upgrade" element={<WebPurchaseRoute><UpgradePage /></WebPurchaseRoute>} />
                <Route path="/upgrade/thank-you" element={<WebPurchaseRoute><ThankYouPage /></WebPurchaseRoute>} />
                <Route path="/about" element={<AboutPage />} />
                <Route path="/trip-ideas" element={<TripIdeasPage />} />
                <Route path="/trip-ideas/:slug" element={<TripGuidePage />} />
                <Route path="/legal/:page" element={<LegalPage />} />
                <Route path="*" element={<NotFoundPage />} />
              </Routes>
            </Suspense>
          </Layout>
        </AuthProvider>
      </Router>
    </Sentry.ErrorBoundary>
  );
}

export default App;
